/**
 * Centralized localStorage and sessionStorage keys for the entire application.
 * Each key includes metadata for automatic Privacy Policy documentation.
 *
 * Usage:
 * ```ts
 * import { STORAGE_KEYS } from '@/lib/storage-keys';
 *
 * localStorage.getItem(STORAGE_KEYS.COLOR_HISTORY.key);
 * localStorage.setItem(STORAGE_KEYS.FAVORITES.key, value);
 * ```
 */

export type StorageType = 'localStorage' | 'sessionStorage';
export type StorageCategory = 'core' | 'tool';

export interface StorageKeyConfig {
  key: string;
  type: StorageType;
  category: StorageCategory;
  title: string;
  description: string;
  highlight?: boolean; // Special visual treatment (blue background for sessionStorage)
}

export const STORAGE_KEYS = {
  // Global app settings
  CONSENT: {
    key: 'kctools-consent',
    type: 'localStorage',
    category: 'core',
    title: 'Storage consent status',
    description: 'Your choice to accept or decline data storage (accepted/declined)',
    highlight: false,
  },
  CONSENT_SESSION: {
    key: 'kctools-consent-session',
    type: 'sessionStorage',
    category: 'core',
    title: 'Declined consent (temporary)',
    description: 'When you decline storage consent, we save this choice in session storage only (not permanent localStorage). This setting disappears when you close the browser tab.',
    highlight: true,
  },
  FAVORITES: {
    key: 'kctools-favorites',
    type: 'localStorage',
    category: 'core',
    title: 'Favorite tools',
    description: 'Your pinned favorite tools appear at the top of the homepage for quick access',
    highlight: false,
  },

  // Tools - Converters
  UNIT_CONVERTER_STATE: {
    key: 'unit-converter-state',
    type: 'localStorage',
    category: 'tool',
    title: 'Unit Converter state',
    description: 'Last selected units and conversion category in Unit Converter',
    highlight: false,
  },
  CURRENCY_CONVERTER_STATE: {
    key: 'currency-converter-state',
    type: 'localStorage',
    category: 'tool',
    title: 'Currency Converter state',
    description: 'Last selected currencies and API provider in Currency Converter',
    highlight: false,
  },
  TABLE_CONVERTER_TEMPLATES: {
    key: 'table-converter-templates',
    type: 'localStorage',
    category: 'tool',
    title: 'Table export templates',
    description: 'Your saved export templates in Table Converter (format, options, preferences)',
    highlight: false,
  },
  TABLE_CONVERTER_URL_HISTORY: {
    key: 'table-converter-url-history',
    type: 'localStorage',
    category: 'tool',
    title: 'Table Converter URL history',
    description: 'Recently imported URLs in Table Converter for quick re-import',
    highlight: false,
  },

  // Tools - Color
  COLOR_HISTORY: {
    key: 'kctools-color-history',
    type: 'localStorage',
    category: 'tool',
    title: 'Color picker history',
    description: 'Recently selected colors in Color Picker and related tools',
    highlight: false,
  },

  // Tools - Crypto
  TOTP_ACCOUNTS: {
    key: 'totp-accounts',
    type: 'localStorage',
    category: 'tool',
    title: 'TOTP accounts',
    description: 'Your encrypted 2FA accounts in TOTP Generator (encrypted with AES-256)',
    highlight: false,
  },
  TOTP_ENCRYPTION_KEY: {
    key: 'totp-encryption-key',
    type: 'localStorage',
    category: 'tool',
    title: 'TOTP encryption key',
    description: 'AES-256 encryption key for TOTP accounts (never transmitted)',
    highlight: false,
  },
  PASSWORD_HISTORY: {
    key: 'kctools-password-history',
    type: 'sessionStorage',
    category: 'tool',
    title: 'Password generation history (temporary)',
    description: 'Recently generated passwords in Password Generator tool. Automatically deleted when you close the tab for security.',
    highlight: true,
  },

  // Tools - Misc
  HTML_ENTITY_FAVORITES: {
    key: 'html-entity-favorites',
    type: 'localStorage',
    category: 'tool',
    title: 'HTML entity favorites',
    description: 'Your favorited HTML entities for quick access',
    highlight: false,
  },

  // Tools - Code
  API_REQUEST_COLLECTIONS: {
    key: 'api-request-builder-collections',
    type: 'localStorage',
    category: 'tool',
    title: 'API request collections',
    description: 'Saved request collections in API Request Builder',
    highlight: false,
  },
  API_REQUEST_HISTORY: {
    key: 'api-request-builder-history',
    type: 'localStorage',
    category: 'tool',
    title: 'API request history',
    description: 'Recent API requests in API Request Builder',
    highlight: false,
  },
  API_REQUEST_ENVIRONMENTS: {
    key: 'api-request-builder-environments',
    type: 'localStorage',
    category: 'tool',
    title: 'API environments',
    description: 'Environment variables (dev, staging, production) in API Request Builder',
    highlight: false,
  },
  WEBSOCKET_PROFILES: {
    key: 'websocket-tester-profiles',
    type: 'localStorage',
    category: 'tool',
    title: 'WebSocket connection profiles',
    description: 'Saved WebSocket connection profiles in WebSocket Tester',
    highlight: false,
  },
  WEBSOCKET_TEMPLATES: {
    key: 'websocket-tester-templates',
    type: 'localStorage',
    category: 'tool',
    title: 'WebSocket message templates',
    description: 'Saved message templates in WebSocket Tester',
    highlight: false,
  },
} as const satisfies Record<string, StorageKeyConfig>;

/**
 * Type-safe storage key accessor - returns just the key string
 */
export type StorageKey = (typeof STORAGE_KEYS)[keyof typeof STORAGE_KEYS]['key'];

/**
 * Helper function to get all keys by storage type
 */
export function getKeysByType(type: StorageType): StorageKeyConfig[] {
  return Object.values(STORAGE_KEYS).filter((config) => config.type === type);
}

/**
 * Helper function to get all keys by category
 */
export function getKeysByCategory(category: StorageCategory): StorageKeyConfig[] {
  return Object.values(STORAGE_KEYS).filter((config) => config.category === category);
}
